'use client'

import { useEffect, useRef } from 'react'

export default function AnimatedLinesCommitment() {
  const greenRef = useRef<HTMLDivElement>(null)
  const redRef = useRef<HTMLDivElement>(null)
  const blueVerticalRef = useRef<HTMLDivElement>(null)
  const blueHorizontalRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasAnimated = useRef(false)

  useEffect(() => {
    if (
      !greenRef.current ||
      !redRef.current ||
      !blueVerticalRef.current ||
      !blueHorizontalRef.current ||
      !containerRef.current ||
      hasAnimated.current
    )
      return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true
            observer.disconnect()

            const greenAnim = greenRef.current?.animate(
              [{ width: '0%' }, { width: '100%' }],
              {
                duration: 1500,
                easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                fill: 'forwards',
              }
            )

            setTimeout(() => {
              redRef.current?.animate([{ width: '0%' }, { width: '100%' }], {
                duration: 1500,
                easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                fill: 'forwards',
              })
            }, 400)

            setTimeout(() => {
              blueHorizontalRef.current?.animate(
                [{ width: '0%' }, { width: '100%' }],
                {
                  duration: 1350,
                  easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
                  fill: 'forwards',
                }
              )
            }, 200)

            setTimeout(() => {
              blueVerticalRef.current?.animate(
                [{ height: '0%' }, { height: '100%' }],
                {
                  duration: 1500,
                  easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
                  fill: 'forwards',
                }
              )
            }, 1200)

            return () => {
              greenAnim?.cancel()
            }
          }
        })
      },
      {
        threshold: 0.01,
      }
    )

    observer.observe(containerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <div ref={containerRef} className="hidden xl:block">
      <div className="absolute bottom-[10rem] left-0 z-0 h-6 w-[12rem] overflow-hidden xl:h-8 xl:w-[18rem]">
        <div
          ref={greenRef}
          className="h-full w-0 origin-left bg-academy-green-500"
        />
      </div>

      <div className="absolute bottom-[25rem] left-0 z-20 h-6 w-[22rem] overflow-hidden xl:h-8 2xl:w-[26rem]">
        <div
          ref={redRef}
          className="h-full w-0 origin-left bg-academy-red-500"
        />
      </div>

      <div className="absolute bottom-[-8.4rem] left-[-20rem] z-10 h-[48rem] w-[20rem] xl:h-[62.5rem] xl:w-[32rem]">
        <div
          ref={blueVerticalRef}
          className="absolute right-0 top-0 h-0 w-[1.5rem] bg-academy-blue-500 xl:w-[2rem]"
        />

        <div
          ref={blueHorizontalRef}
          className="absolute left-0 top-0 h-[1.5rem] w-0 bg-academy-blue-500 xl:h-[2rem]"
        />
      </div>
    </div>
  )
}
